/**
 * Reference data, served as one payload.
 *
 * The layout route loads this once for the whole CRM subtree — three separate
 * endpoints would be three round trips for data that is small, changes rarely,
 * and is always needed together.
 */

import { z } from 'zod'
import { instant, uuid } from './common'
import { leadStatus } from './lead'
import { dealStatus } from './deal'
import { crmUser } from './user'

/** Shape shared by the lookups that are just a name and an order. */
export const lookupItem = z.object({
  id: uuid,
  name: z.string(),
  position: z.number().int(),
})
export type LookupItem = z.infer<typeof lookupItem>

export const lookups = z.object({
  leadStatuses: z.array(leadStatus),
  dealStatuses: z.array(dealStatus),
  leadSources: z.array(lookupItem),
  lostReasons: z.array(lookupItem),
  industries: z.array(lookupItem),
  /**
   * From the host app's directory, not a CRM table — see the UserDirectory port.
   * Bundled here so every owner and assignee picker renders without its own
   * request. Fine to a few hundred people, which is the shape of an internal
   * ERP; past that, split it out behind a search endpoint.
   */
  users: z.array(crmUser),
})
export type Lookups = z.infer<typeof lookups>

/**
 * Lookups a user may extend from a picker.
 *
 * Statuses are deliberately absent. Industries and sources are descriptive — a
 * long list is untidy but harmless. Statuses are structural: the board, the
 * conversion boundary and the terminal-reason rule all read them, and letting
 * anyone add one is how a pipeline grows back to thirty-five stages. Those stay
 * an admin operation.
 */
export const extensibleLookup = z.enum(['industry', 'lead_source', 'lost_reason'])
export type ExtensibleLookup = z.infer<typeof extensibleLookup>

export const createLookupInput = z
  .object({
    kind: extensibleLookup,
    name: z.string().trim().min(1).max(120),
  })
  .strict()
export type CreateLookupInput = z.infer<typeof createLookupInput>

/**
 * Admin editing of reference data. Archiving rather than deleting is enforced by
 * the schema — leads and deals hold foreign keys to these rows.
 */
export const upsertLookupInput = z
  .object({
    name: z.string().trim().min(1).max(120),
    position: z.number().int().nonnegative(),
  })
  .strict()
export type UpsertLookupInput = z.infer<typeof upsertLookupInput>

export const archivableLookupItem = lookupItem.extend({
  archivedAt: instant.nullable(),
})
export type ArchivableLookupItem = z.infer<typeof archivableLookupItem>
